import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, User, MapPin, Instagram, Copy, Share2, Award, BookOpen,
  Trophy, Users, ShieldAlert, Sparkles, Handshake,
} from "lucide-react";
import SEO from "@/components/SEO";
import ProfileBanner from "@/components/ProfileBanner";
import CollectionAlbumSnapshotCard from "@/components/collector/CollectionAlbumSnapshotCard";
import { buildAlbumPresentation } from "@/lib/albumPresentation";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  collecting: { label: "Collecting", tone: "yellow" },
  completed:  { label: "Completed",  tone: "green"  },
  archived:   { label: "Archived",   tone: "gray"   },
  sold:       { label: "Sold",       tone: "blue"   },
};

export default function CollectorPublicProfile() {
  const { slug } = useParams();
  const username = (slug || "").toLowerCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/collectors/${username}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setData({ ok: false, profile: null, private: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center bg-[var(--cream)]" data-testid="public-profile-loading">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }
  const isPrivate = !data?.profile || data?.private;
  if (isPrivate) return <PrivateOrMissing username={username} />;

  const p = data.profile;
  const avatarSrc = p.avatar_media_id
    ? `${API}/public/collectors/${username}/avatar?v=${encodeURIComponent(p.avatar_updated_at || "")}`
    : null;
  const bannerSrc = p.banner_media_id
    ? `${API}/public/collectors/${username}/banner?v=${encodeURIComponent(p.banner_updated_at || "")}`
    : null;
  const shareUrl = `${window.location.origin}/collectors/${username}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${p.display_name} · CollectorUp`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Profile link copied.");
      }
    } catch { /* user cancelled */ }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Profile link copied.");
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="public-profile-page">
      <SEO title={`${p.display_name || username}`} />

      {/* Iter QA #007 — banner via shared ProfileBanner: object-contain,
          centered, complete artwork visible, blurred backdrop fills letterbox. */}
      <ProfileBanner src={bannerSrc} testid="public-profile-banner" />

      <div className="container-gm -mt-14 md:-mt-16 relative z-10">
        {/* Header card */}
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-5 md:p-6 shadow-[4px_4px_0_var(--ink)]" data-testid="public-profile-header">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] overflow-hidden grid place-items-center shrink-0">
              {avatarSrc
                ? <img src={avatarSrc} alt={`${p.display_name} avatar`} className="w-full h-full object-cover" data-testid="public-profile-avatar" />
                : <User size={32} className="text-[var(--ink)]/40" data-testid="public-profile-avatar-placeholder" />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="public-profile-name">{p.display_name || username}</h1>
              <div className="mt-1 flex items-center gap-3 text-sm text-[var(--smoke)] flex-wrap">
                <span className="font-mono text-[var(--ink)]" data-testid="public-profile-username">@{p.username}</span>
                {p.public_collector_id && <span className="font-mono">{p.public_collector_id}</span>}
                {p.member_since && <span>Member since {new Date(p.member_since).toLocaleDateString(undefined, { year: "numeric", month: "short" })}</span>}
              </div>
              {p.bio && <p className="mt-3 text-sm text-[var(--ink)]" data-testid="public-profile-bio">{p.bio}</p>}
              <div className="mt-3 flex items-center gap-3 text-xs text-[var(--smoke)] flex-wrap">
                {p.general_location && (
                  <span className="inline-flex items-center gap-1" data-testid="public-profile-location">
                    <MapPin size={12} /> {p.general_location}
                  </span>
                )}
                {/* Phase 1e.1 — replaces favorite_category display */}
                {p.favorite_club_team && (
                  <span className="inline-flex items-center gap-1" data-testid="public-profile-club">
                    <Sparkles size={12} /> {p.favorite_club_team}
                  </span>
                )}
                {p.favorite_national_team && (
                  <span className="inline-flex items-center gap-1" data-testid="public-profile-national">
                    <Sparkles size={12} /> {p.favorite_national_team}
                  </span>
                )}
                {p.instagram_handle && (
                  <a href={`https://instagram.com/${encodeURIComponent(p.instagram_handle)}`}
                     target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 hover:text-[var(--panini-red)] font-bold"
                     data-testid="public-profile-instagram">
                    <Instagram size={12} /> @{p.instagram_handle}
                  </a>
                )}
                {p.tiktok_handle && (
                  <a href={`https://tiktok.com/@${encodeURIComponent(p.tiktok_handle)}`}
                     target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 hover:text-[var(--panini-red)] font-bold"
                     data-testid="public-profile-tiktok">
                    <Sparkles size={12} /> TikTok
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" onClick={share} className="btn-red text-xs" data-testid="public-profile-share">
                <Share2 size={12} /> Share
              </button>
              <button type="button" onClick={copy} className="btn-ghost text-xs" data-testid="public-profile-copy">
                <Copy size={12} /> Copy link
              </button>
            </div>
          </div>
        </div>

        {/* Iter QA #003 — Achievements moved above Statistics so the
            collector's identity is emphasised before their numbers. */}
        {p.privacy?.show_achievements && (p.achievements || []).length > 0 && (
          <Section title="Achievements" icon={Trophy} testid="public-profile-achievements-section">
            <div className="flex flex-wrap gap-2">
              {p.achievements.map((a) => (
                <div key={a.badge_id || a.id}
                     className="px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] text-xs font-bold">
                  {a.label || a.badge_id}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Statistics */}
        {p.album_stats && (
          <Section title="Collection statistics" testid="public-profile-stats-section">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatTile label="Currently Owned" value={p.album_stats.albums_currently_owned} testid="stat-owned" />
              <StatTile label="Collecting"      value={p.album_stats.albums_collecting}      testid="stat-collecting" />
              <StatTile label="Completed"       value={p.album_stats.albums_completed}       testid="stat-completed" />
              <StatTile label="Sold"            value={p.album_stats.albums_sold}            testid="stat-sold" />
              <StatTile label="Total Ever"      value={p.album_stats.albums_total_ever_registered} testid="stat-total" />
            </div>
          </Section>
        )}

        {/* Album library — Phase C2b: excludes completed albums (which
            now live in the Trophy Case section below). Shows in-progress
            and collection-only public albums using the shared compact
            horizontal card. */}
        <PublicAlbumLibrary
          albums={p.albums || []}
          username={username}
        />

        {/* Trophy Case — Phase C2b. Contains two subgroups:
              1. Completed Albums (public completed albums only)
              2. Verified Certificates (existing public certs)
            Renders whenever either subgroup has items. Certificates
            without an album association are still shown. */}
        <PublicTrophyCase
          albums={p.albums || []}
          certificates={p.certificates || []}
          showCertificates={p.privacy?.show_certificates}
          username={username}
        />

        {/* Trading history placeholder */}
        <Section title="Trading history" icon={Handshake} testid="public-profile-trading-section">
          <EmptyState icon={Handshake} title="Trading history will appear here."
            subtitle="This feature is on the CollectorUp roadmap." testid="public-profile-trading-empty" />
        </Section>

        <div className="py-10 text-center">
          <Link to="/collectors" className="text-xs text-[var(--smoke)] hover:text-[var(--ink)] underline">
            Browse the Collector Registry
          </Link>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Phase C2b · Public Album Library — in-progress + collection-only.
// Completed albums are hoisted into the Trophy Case section below to
// avoid duplication (per §5 of the C2b brief).
// ══════════════════════════════════════════════════════════════════
export function PublicAlbumLibrary({ albums, username }) {
  const nonCompleted = useMemo(
    () => albums.filter((a) => a.status !== "completed"),
    [albums]
  );
  return (
    <Section title="Album library" icon={BookOpen} testid="public-profile-albums-section">
      {nonCompleted.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No public albums yet."
          subtitle="When this collector marks albums as visible, they'll appear here."
          testid="public-profile-albums-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {nonCompleted.map((a) => (
            <PublicAlbumCard key={a.id} album={a} username={username} />
          ))}
        </div>
      )}
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Phase C2b · Trophy Case — public completed albums + verified certs.
// Renders whenever at least one subgroup has items. Each subgroup
// renders only when it has items, so we never show empty headings.
// Certificates without an album association still render.
// ══════════════════════════════════════════════════════════════════
export function PublicTrophyCase({ albums, certificates, showCertificates, username }) {
  const completedAlbums = useMemo(
    () => albums.filter((a) => a.status === "completed"),
    [albums]
  );
  // Album ids already surfaced as completed cards — used to dedupe the
  // certificate list so an associated certificate that is *already*
  // rendered as a link on its album card doesn't ALSO show up as a
  // separate certificate tile.
  const completedAlbumCertNumbers = useMemo(
    () => new Set(
      completedAlbums
        .map((a) => a.certificate_number)
        .filter(Boolean)
    ),
    [completedAlbums]
  );
  const visibleCerts = showCertificates ? (certificates || []) : [];
  const unassociatedCerts = useMemo(
    () => visibleCerts.filter((c) => !completedAlbumCertNumbers.has(c.cert_number)),
    [visibleCerts, completedAlbumCertNumbers]
  );

  const hasCompleted = completedAlbums.length > 0;
  const hasCerts = visibleCerts.length > 0;
  if (!hasCompleted && !hasCerts) return null;

  return (
    <section className="mt-6" data-testid="public-profile-trophy-case">
      <div className="flex items-center gap-2 mb-4">
        <Trophy size={16} className="text-[#c8a24d]" />
        <h2 className="font-display text-2xl">Trophy Case</h2>
      </div>

      {hasCompleted && (
        <div className="mb-6" data-testid="public-profile-trophy-completed">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8a6a1f] mb-3">
            Completed Albums
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {completedAlbums.map((a) => (
              <PublicCompletedAlbumCard key={a.id} album={a} username={username} />
            ))}
          </div>
        </div>
      )}

      {showCertificates && hasCerts && (
        <div data-testid="public-profile-trophy-certs">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#8a6a1f] mb-3">
            Verified Certificates
          </div>
          {unassociatedCerts.length === 0 ? (
            <EmptyState
              icon={Award}
              title="All certificates are shown on their trophy cards."
              subtitle="Every verified certificate is linked to a completed album above."
              testid="public-profile-trophy-certs-empty"
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {unassociatedCerts.map((c) => (
                <PublicCertificateTile key={c.cert_number} cert={c} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PublicCompletedAlbumCard({ album, username }) {
  const coverOverride = album.cover_image_media_id
    ? `${API}/public/collectors/${username}/albums/${album.id}/cover?v=${encodeURIComponent(album.cover_image_updated_at || "")}`
    : null;
  return (
    <div data-testid={`public-trophy-album-${album.id}`}>
      <CollectionAlbumSnapshotCard
        album={coverOverride ? { ...album, cover_image_url: coverOverride } : album}
        variant="trophy"
        layout="horizontal"
        // Phase C2b remediation — Public Trophy Case cards must
        // visibly show canonical Collected + Missing counts alongside
        // the 100% badge. Duplicates are private inventory data and
        // stay hidden on the public payload.
        showCollected
        showMissing
        showDuplicates={false}
        footer={
          album.certificate_number ? (
            <a
              href={`/certificates/${album.certificate_number}`}
              className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-emerald-900 hover:underline"
              data-testid={`public-trophy-cert-${album.id}`}
            >
              <Award size={12} /> View Certificate
            </a>
          ) : album.completion_date ? (
            <div
              className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8a6a1f]"
              data-testid={`public-trophy-date-${album.id}`}
            >
              Completed {new Date(album.completion_date).toLocaleDateString(undefined, {
                year: "numeric", month: "short",
              })}
            </div>
          ) : null
        }
      />
    </div>
  );
}

function PublicCertificateTile({ cert }) {
  const date = cert.completion_date || cert.issued_at;
  return (
    <a
      href={`/certificates/${cert.cert_number}`}
      className="block p-4 rounded-2xl border-2 border-[#c8a24d]/60 bg-gradient-to-br from-white to-[#fffaf0] hover:brightness-105 transition"
      data-testid={`public-cert-tile-${cert.cert_number}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[#8a6a1f]">
        <Award size={11} /> Verified Certificate
      </div>
      <div className="mt-1 font-mono font-bold text-base text-[var(--ink)]">
        {cert.cert_number}
      </div>
      {cert.player_name && (
        <div className="mt-1 text-xs text-[var(--smoke)]">{cert.player_name}</div>
      )}
      {cert.album_title_snapshot && (
        <div className="mt-1 text-xs text-[var(--smoke)] truncate">
          {cert.album_title_snapshot}
        </div>
      )}
      {date && (
        <div className="mt-1 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-800">
          {new Date(date).toLocaleDateString(undefined, {
            year: "numeric", month: "short",
          })}
        </div>
      )}
    </a>
  );
}

function PublicAlbumCard({ album, username }) {
  // Prefer the collector's private uploaded cover (public-safe URL
  // proxied through the phase4 avatar/album-cover endpoint) then fall
  // through to the catalog cover URL that the backend already resolved.
  const coverOverride = album.cover_image_media_id
    ? `${API}/public/collectors/${username}/albums/${album.id}/cover?v=${encodeURIComponent(album.cover_image_updated_at || "")}`
    : null;
  const p = buildAlbumPresentation(album);
  return (
    <div data-testid={`public-album-${album.id}`}>
      <CollectionAlbumSnapshotCard
        album={coverOverride ? { ...album, cover_image_url: coverOverride } : album}
        layout="horizontal"
        // In-progress cards keep their counts; collection-only cards
        // automatically hide them via the shared card.
        showCounts={p.tracksProgress}
      />
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="mt-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={14} className="text-[var(--panini-red)]" />}
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-xl border-2 border-[var(--ink)] bg-white p-3 shadow-[2px_2px_0_var(--ink)]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</div>
      <div className="mt-1 font-display text-2xl">{value ?? 0}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle, testid }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--ink)] bg-white p-8 text-center" data-testid={testid}>
      <Icon size={26} className="mx-auto text-[var(--smoke)]" />
      <div className="font-display text-lg mt-2">{title}</div>
      <p className="text-xs text-[var(--smoke)] mt-1 max-w-sm mx-auto">{subtitle}</p>
    </div>
  );
}

function PrivateOrMissing({ username }) {
  return (
    <div className="min-h-[70vh] grid place-items-center bg-[var(--cream)]" data-testid="public-profile-private">
      <SEO title="Private profile" />
      <div className="max-w-md text-center p-8">
        <ShieldAlert size={36} className="mx-auto text-[var(--smoke)]" />
        <h1 className="font-display text-3xl mt-3">This profile is private.</h1>
        <p className="text-sm text-[var(--smoke)] mt-2">
          <span className="font-mono text-[var(--ink)]">@{username}</span> hasn&apos;t made their CollectorUp profile public,
          or the profile does not exist.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link to="/collectors" className="btn-ghost text-sm">
            <Users size={13} /> Browse Collector Registry
          </Link>
        </div>
      </div>
    </div>
  );
}
